import React from 'react';
import styled from 'styled-components';

const DocsPage = styled.div`
  max-width: 800px;
  margin: 0 auto;
  
  h1 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 2.5rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  
  h2 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.8rem;
    font-weight: 600;
    color: #ffffff;
    margin: 2rem 0 1rem 0;
    line-height: 1.3;
  }
  
  h3 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.4rem;
    font-weight: 600;
    color: #ffffff;
    margin: 1.5rem 0 0.75rem 0;
    line-height: 1.4;
  }
  
  p {
    font-family: 'Roboto', sans-serif;
    font-size: 1rem;
    line-height: 1.6;
    color: #e5e7eb;
    margin-bottom: 1rem;
  }
  
  ul, ol {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }
  
  li {
    font-family: 'Roboto', sans-serif;
    font-size: 1rem;
    line-height: 1.6;
    color: #e5e7eb;
    margin-bottom: 0.5rem;
  }
  
  code {
    font-family: 'monospace', sans-serif;
    background: rgba(0, 0, 0, 0.3);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.9em;
    color: #a855f7;
  }
  
  .contract-info {
    background: rgba(16, 185, 129, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
    margin: 2rem 0;
  }
  
  .contract-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    color: #10b981;
    margin-bottom: 1rem;
  }
  
  .contract-address {
    font-family: 'monospace', sans-serif;
    font-size: 0.9rem;
    color: #ffffff;
    background: rgba(0, 0, 0, 0.3);
    padding: 0.75rem;
    border-radius: 8px;
    margin: 0.75rem 0;
    word-break: break-all;
  }
  
  .feature-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;
  }
  
  .feature-card {
    background: rgba(168, 85, 247, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
  }
  
  .feature-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    color: #a855f7;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .step {
    background: rgba(168, 85, 247, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
    margin: 1.5rem 0;
  }
  
  .step-number {
    background: #a855f7;
    color: #ffffff;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }
  
  .step-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 0.75rem;
  }
  
  .step-content {
    font-family: 'Inter', sans-serif;
    color: #e5e7eb;
    line-height: 1.6;
  }
  
  .info-box {
    background: rgba(59, 130, 246, 0.1);
    border-radius: 8px;
    padding: 1rem;
    margin: 1rem 0;
  }
  
  .info-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    color: #3b82f6;
    margin-bottom: 0.5rem;
  }
  
  .info-text {
    font-family: 'Inter', sans-serif;
    color: #d1d5db;
    font-size: 0.95rem;
  }
  
  .highlight-box {
    background: linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%);
    border-radius: 12px;
    padding: 1.5rem;
    margin: 2rem 0;
  }
  
  .highlight-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.3rem;
    font-weight: 700;
    color: #a855f7;
    margin-bottom: 1rem;
  }
  
  .comparison-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin: 2rem 0;
  }
  
  .comparison-card {
    background: rgba(59, 130, 246, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
  }
  
  .comparison-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    color: #3b82f6;
    margin-bottom: 1rem;
    text-align: center;
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 2rem;
    }
    
    h2 {
      font-size: 1.5rem;
    }
    
    .feature-grid {
      grid-template-columns: 1fr;
    }
    
    .comparison-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const TokenTrading: React.FC = () => {
  return (
    <DocsPage>
      <h1>Token Trading on Mavrk</h1>
      
      <p>
        The Mavrk trading system leverages direct smart contract interaction with Uniswap V3's SwapRouter02 contract to provide the most efficient trading experience for all tokens deployed on the platform. By utilizing the unique 100% liquidity model, Mavrk ensures optimal pricing and execution for every trade.
      </p>

      <div className="contract-info">
        <div className="contract-title">SwapRouter02 Contract</div>
        <p><strong>Contract Address:</strong></p>
        <div className="contract-address">0x177778F19E89dD1012BdBe603F144088A95C4B53</div>
        <p><strong>Network:</strong> Ink L2 (Chain ID: 57073)</p>
        <p><strong>Purpose:</strong> Direct token swaps with optimal routing</p>
        <p><strong>Protocol:</strong> Uniswap V3</p>
        <p><strong>Explorer:</strong> <a href="https://explorer.inkonchain.com/address/0x177778F19E89dD1012BdBe603F144088A95C4B53" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa' }}>View on Explorer</a></p>
      </div>

      <h2>How Mavrk Trading Works</h2>

      <p>
        Unlike traditional DEX aggregators that route trades across multiple pools to find the best price, Mavrk's trading system takes advantage of a fundamental truth: <strong>when 100% of a token's supply is in a single liquidity pool, that pool IS the best price</strong>.
      </p>

      <div className="highlight-box">
        <div className="highlight-title">🎯 Direct Execution Advantage</div>
        <p>
          The Mavrk platform calls the <code>exactInputSingle</code> function of the SwapRouter02 contract directly for each individual V3 liquidity pool. This approach offers several key advantages:
        </p>
        <ul>
          <li><strong>Zero Route Searching:</strong> No need to search for optimal paths across multiple pools</li>
          <li><strong>Lowest Gas Costs:</strong> Single-hop swaps minimize gas consumption</li>
          <li><strong>Best Pricing Guaranteed:</strong> 100% of liquidity means this IS the market price</li>
          <li><strong>Maximum Efficiency:</strong> Direct contract calls eliminate intermediate steps</li>
          <li><strong>Predictable Execution:</strong> No unexpected routing or slippage from complex paths</li>
        </ul>
      </div>

      <h2>The 100% Liquidity Advantage</h2>

      <p>
        Every token deployed on Mavrk has its <strong>entire 1 billion token supply</strong> paired with WETH in a single Uniswap V3 concentrated liquidity pool. This unique structure creates several trading advantages:
      </p>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">💧 Deep Liquidity</h3>
          <ul>
            <li>100% of token supply available for trading</li>
            <li>No fragmented liquidity across multiple pools</li>
            <li>Consistent pricing for all trade sizes</li>
            <li>Reduced slippage even for large trades</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">⚡ Optimal Routing</h3>
          <ul>
            <li>Single pool = single best route</li>
            <li>No need for multi-hop routing</li>
            <li>Lowest possible gas costs</li>
            <li>Fastest execution times</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">📊 Price Discovery</h3>
          <ul>
            <li>True market price in one location</li>
            <li>No arbitrage opportunities between pools</li>
            <li>Transparent price formation</li>
            <li>Accurate real-time pricing</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">🔒 Security</h3>
          <ul>
            <li>Direct contract interaction (no intermediaries)</li>
            <li>Verifiable on-chain execution</li>
            <li>No routing manipulation possible</li>
            <li>Transparent transaction flow</li>
          </ul>
        </div>
      </div>

      <h2>Direct Contract Interaction</h2>

      <div className="step">
        <div className="step-number">1</div>
        <div>
          <h3 className="step-title">User Initiates Trade</h3>
          <div className="step-content">
            <p>When a user wants to trade a Mavrk token:</p>
            <ul>
              <li>User selects token to buy or sell</li>
              <li>Enters desired amount</li>
              <li>Reviews price impact and estimated output</li>
              <li>Sets acceptable slippage tolerance</li>
              <li>Confirms transaction in wallet</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">2</div>
        <div>
          <h3 className="step-title">Platform Calls SwapRouter02</h3>
          <div className="step-content">
            <p>The Mavrk platform directly calls the SwapRouter02 contract:</p>
            <ul>
              <li><strong>Function:</strong> <code>exactInputSingle</code></li>
              <li><strong>Pool Specification:</strong> Direct pool address for the token pair</li>
              <li><strong>Amount:</strong> Exact input amount from user</li>
              <li><strong>Recipient:</strong> User's wallet address</li>
              <li><strong>Deadline:</strong> Transaction expiry timestamp</li>
              <li><strong>Slippage:</strong> Minimum output amount (amountOutMinimum)</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">3</div>
        <div>
          <h3 className="step-title">SwapRouter02 Executes Trade</h3>
          <div className="step-content">
            <p>The SwapRouter02 contract processes the swap:</p>
            <ul>
              <li>Validates trade parameters</li>
              <li>Interacts directly with the specified Uniswap V3 pool</li>
              <li>Executes swap at current pool price</li>
              <li>Collects 0.3% trading fee for liquidity providers</li>
              <li>Transfers output tokens to user's wallet</li>
              <li>Emits <code>Swap</code> event for transparency</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">4</div>
        <div>
          <h3 className="step-title">Confirmation and Tracking</h3>
          <div className="step-content">
            <p>After successful execution:</p>
            <ul>
              <li>User receives exact output amount</li>
              <li>Transaction hash provided for verification</li>
              <li>Direct link to block explorer</li>
              <li>Updated balance shown immediately</li>
              <li>USD value calculated in real-time</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>Comparison: Mavrk vs Traditional DEX Aggregators</h2>

      <div className="comparison-grid">
        <div className="comparison-card">
          <div className="comparison-title">Traditional DEX Aggregators</div>
          <ul>
            <li>Must search across multiple pools</li>
            <li>Complex routing algorithms needed</li>
            <li>Multi-hop swaps increase gas costs</li>
            <li>Fragmented liquidity across pools</li>
            <li>Potential for front-running on routes</li>
            <li>Higher computational overhead</li>
            <li>Inconsistent pricing across venues</li>
            <li>More points of failure in route</li>
          </ul>
        </div>

        <div className="comparison-card">
          <div className="comparison-title">Mavrk Direct Execution</div>
          <ul>
            <li><strong>Single pool</strong> - no searching required</li>
            <li><strong>Simple direct call</strong> - no complex routing</li>
            <li><strong>Single-hop swaps</strong> - minimum gas usage</li>
            <li><strong>100% liquidity</strong> in one location</li>
            <li><strong>Direct execution</strong> - reduced MEV exposure</li>
            <li><strong>Minimal overhead</strong> - fast execution</li>
            <li><strong>True market price</strong> - no arbitrage</li>
            <li><strong>Single point of execution</strong> - reliable</li>
          </ul>
        </div>
      </div>

      <h2>Trading Features</h2>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">💰 Buy Tokens</h3>
          <p><strong>Process:</strong></p>
          <ol>
            <li>Select token to buy</li>
            <li>Enter ETH or WETH amount to spend</li>
            <li>Review estimated token output</li>
            <li>Confirm trade in wallet</li>
            <li>Receive tokens instantly</li>
          </ol>
          <div className="info-box">
            <div className="info-text">
              Platform collects 1% service fee on the input amount to facilitate the trade and maintain infrastructure.
            </div>
          </div>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">💸 Sell Tokens</h3>
          <p><strong>Process:</strong></p>
          <ol>
            <li>Select token to sell</li>
            <li>Enter token amount to sell</li>
            <li>Review estimated ETH/WETH output</li>
            <li>Confirm trade in wallet</li>
            <li>Receive ETH/WETH instantly</li>
          </ol>
          <div className="info-box">
            <div className="info-text">
              Platform collects 1% service fee on the input amount. The 99% is swapped for optimal user value.
            </div>
          </div>
        </div>
      </div>

      <h2>Technical Implementation</h2>

      <h3>exactInputSingle Parameters</h3>

      <div className="step">
        <div className="step-content">
          <p>The Mavrk platform constructs the following parameters for SwapRouter02:</p>
          <ul>
            <li><strong>tokenIn:</strong> Address of input token (e.g., WETH for buys, token for sells)</li>
            <li><strong>tokenOut:</strong> Address of output token (e.g., token for buys, WETH for sells)</li>
            <li><strong>fee:</strong> 3000 (0.3% fee tier)</li>
            <li><strong>recipient:</strong> User's wallet address</li>
            <li><strong>deadline:</strong> Current timestamp + reasonable buffer (e.g., 20 minutes)</li>
            <li><strong>amountIn:</strong> Exact input amount (after platform's 1% service fee)</li>
            <li><strong>amountOutMinimum:</strong> Calculated based on current price and user's slippage tolerance</li>
            <li><strong>sqrtPriceLimitX96:</strong> 0 (no price limit, use slippage protection instead)</li>
          </ul>
        </div>
      </div>

      <h3>Price Calculation</h3>

      <div className="step">
        <div className="step-content">
          <p>Real-time pricing is obtained through:</p>
          <ul>
            <li><strong>slot0 Query:</strong> Direct call to pool's <code>slot0</code> function</li>
            <li><strong>Current Price:</strong> Extracted from <code>sqrtPriceX96</code> value</li>
            <li><strong>Price Impact:</strong> Calculated based on trade size vs total liquidity</li>
            <li><strong>USD Conversion:</strong> ETH price fetched from reliable oracle</li>
            <li><strong>Real-time Updates:</strong> Price refreshes before each trade confirmation</li>
          </ul>
        </div>
      </div>

      <h2>Trading Advantages</h2>

      <div className="highlight-box">
        <div className="highlight-title">Why Direct Execution is Superior</div>
        <ol>
          <li>
            <strong>Guaranteed Best Price:</strong> With 100% of tokens in one pool, there's no better price elsewhere to find
          </li>
          <li>
            <strong>Lower Gas Costs:</strong> Single-hop direct execution uses significantly less gas than multi-hop aggregator routes
          </li>
          <li>
            <strong>Faster Execution:</strong> No time wasted searching for routes or comparing multiple pools
          </li>
          <li>
            <strong>Reduced Complexity:</strong> Simpler execution path means fewer potential points of failure
          </li>
          <li>
            <strong>Better Predictability:</strong> Direct pool interaction provides consistent, predictable results
          </li>
          <li>
            <strong>Enhanced Security:</strong> Fewer contract interactions reduce attack surface and MEV exposure
          </li>
          <li>
            <strong>Complete Transparency:</strong> Users can verify exact execution path on-chain
          </li>
        </ol>
      </div>

      <h2>Slippage Protection</h2>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">Automatic Protection</h3>
          <ul>
            <li>Default slippage tolerance set by platform</li>
            <li>Real-time price impact calculation</li>
            <li>Warning for high-impact trades</li>
            <li>Transaction reverts if price moves beyond tolerance</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">Custom Settings</h3>
          <ul>
            <li>Users can adjust slippage tolerance</li>
            <li>Higher tolerance for volatile markets</li>
            <li>Lower tolerance for stable conditions</li>
            <li>Clear explanation of trade-offs</li>
          </ul>
        </div>
      </div>

      <h2>Fee Structure</h2>

      <div className="step">
        <div className="step-content">
          <h4>Uniswap V3 Pool Fee: 0.3%</h4>
          <ul>
            <li>Standard Uniswap V3 fee tier</li>
            <li>Collected automatically on every swap</li>
            <li>Distributed to liquidity providers (via MavrkLocker)</li>
            <li>Split: 25% to creators, 75% to platform operations</li>
          </ul>

          <h4>Mavrk Service Fee: 1%</h4>
          <ul>
            <li>Applied to input amount before swap</li>
            <li>Covers platform infrastructure and development</li>
            <li>Enables gas sponsorship and advanced features</li>
            <li>Transparent and clearly displayed before trade</li>
          </ul>
        </div>
      </div>

      <h2>Transaction Verification</h2>

      <p>
        Every trade executed on Mavrk can be fully verified on-chain:
      </p>

      <ul>
        <li><strong>Transaction Hash:</strong> Unique identifier for each trade</li>
        <li><strong>Block Explorer Link:</strong> Direct link to view full transaction details</li>
        <li><strong>Input/Output Amounts:</strong> Exact amounts visible on-chain</li>
        <li><strong>Gas Used:</strong> Actual gas consumption displayed</li>
        <li><strong>Timestamp:</strong> Exact execution time</li>
        <li><strong>Pool Interaction:</strong> Specific pool address and state changes</li>
        <li><strong>Events Emitted:</strong> Swap events with complete details</li>
      </ul>

      <div className="info-box">
        <div className="info-title">Pro Tip</div>
        <div className="info-text">
          The direct execution model used by Mavrk isn't just more efficient—it's fundamentally superior for tokens with concentrated liquidity. When 100% of a token's supply is in one pool, that pool defines the true market price, making route aggregation unnecessary and adding only gas costs without value. This is why Mavrk's direct approach offers the best possible trading experience.
        </div>
      </div>
    </DocsPage>
  );
};

export default TokenTrading;
